import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, FileText, Camera, PenTool, User, ArrowLeft } from 'lucide-react';

/**
 * ReviewSubmitStep Component
 * 
 * Step 5 of Physician ID Proofing: Review & Submit All Collected Information
 * 
 * Features:
 * - Display summary of all captured data (NPI, Driver's License, Selfie, Signature)
 * - Preview images and files with basic validation status
 * - Submit all data to backend API for human review
 * - Loading states during submission
 * - Error handling and retry functionality
 * - Accessibility and keyboard navigation
 * - Confirmation modal before submission
 */

interface ReviewSubmitStepProps {
  npi: string;
  driversLicense?: File | null;
  selfiePhoto?: Blob | null;
  signature?: Blob | null;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

interface SummaryItemProps {
  icon: React.ReactNode;
  title: string;
  status: 'complete' | 'missing';
  preview?: React.ReactNode;
  description: string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ icon, title, status, preview, description }) => (
  <div className="border rounded-lg p-4 bg-white shadow-sm">
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-full ${status === 'complete' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-medium text-gray-900">{title}</h3>
          {status === 'complete' ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <AlertCircle className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">{description}</p>
        {preview && (
          <div className="mt-2">
            {preview}
          </div>
        )}
      </div>
    </div>
  </div>
);

export const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({
  npi,
  driversLicense,
  selfiePhoto,
  signature,
  onBack,
  onSubmit,
  isSubmitting
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check if all required data is present
  const isComplete = npi && driversLicense && selfiePhoto && signature;

  // Debug logging
  console.log('📋 ReviewSubmitStep props:', {
    npi: !!npi,
    driversLicense: !!driversLicense,
    selfiePhoto: !!selfiePhoto,
    signature: !!signature,
    isComplete
  });

  const handleSubmitClick = () => {
    if (isComplete) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmSubmit = () => {
    setShowConfirmation(false);
    onSubmit();
  };

  // Create preview URLs for images
  const selfieUrl = selfiePhoto ? URL.createObjectURL(selfiePhoto) : null;
  const signatureUrl = signature ? URL.createObjectURL(signature) : null;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Information</h2>
        <p className="text-gray-600">
          Please review all the information you've provided. Once submitted, this will be sent for verification.
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {/* NPI Summary */}
        <SummaryItem
          icon={<User className="h-5 w-5" />}
          title="NPI Number"
          status={npi ? 'complete' : 'missing'}
          description="Your National Provider Identifier"
          preview={
            npi ? (
              <div className="bg-gray-50 p-3 rounded border text-sm font-mono">
                {npi}
              </div>
            ) : null
          }
        />

        {/* Driver's License Summary */}
        <SummaryItem
          icon={<FileText className="h-5 w-5" />}
          title="Driver's License"
          status={driversLicense ? 'complete' : 'missing'}
          description="Photo identification document"
          preview={
            driversLicense ? (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <FileText className="h-4 w-4" />
                <span>{driversLicense.name}</span>
                <span className="text-gray-500">({(driversLicense.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            ) : null
          }
        />

        {/* Selfie Summary */}
        <SummaryItem
          icon={<Camera className="h-5 w-5" />}
          title="Identity Photo"
          status={selfiePhoto ? 'complete' : 'missing'}
          description="Selfie photo for identity verification"
          preview={
            selfieUrl ? (
              <div className="w-24 h-24 rounded-lg overflow-hidden border">
                <img 
                  src={selfieUrl} 
                  alt="Identity photo preview" 
                  className="w-full h-full object-cover"
                />
              </div>
            ) : null
          }
        />

        {/* Signature Summary */}
        <SummaryItem
          icon={<PenTool className="h-5 w-5" />}
          title="Electronic Signature"
          status={signature ? 'complete' : 'missing'}
          description="Digital signature for identity confirmation"
          preview={
            signatureUrl ? (
              <div className="w-48 h-20 rounded border bg-white overflow-hidden">
                <img 
                  src={signatureUrl} 
                  alt="Signature preview" 
                  className="w-full h-full object-contain"
                />
              </div>
            ) : null
          }
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        
        <button
          onClick={handleSubmitClick}
          disabled={!isComplete || isSubmitting}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit for Verification'
          )}
        </button>
      </div>

      {!isComplete && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Please complete all steps before submitting</span>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Submission</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to submit your ID proofing information? This will send your documents for human verification.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 